import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tag, Check, X, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";

const STATUS_PILL = {
  pending: { label: "Pending", cls: "pill--open" },
  countered: { label: "Countered", cls: "pill--open" },
  accepted: { label: "Accepted", cls: "pill--delivered" },
  declined: { label: "Declined", cls: "pill--refunded" },
};

function OfferCard({ o, mode, onAction }) {
  const navigate = useNavigate();
  const pill = STATUS_PILL[o.status] || { label: o.status, cls: "" };
  const canRespond = mode === "received" && ["pending", "countered"].includes(o.status);
  return (
    <div className="ss-card" style={{ padding: 16, display: "flex", gap: 14, alignItems: "flex-start" }} data-testid={`offer-${o.id}`}>
      {o.item_image ? <img src={o.item_image} alt={o.item_title} style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} /> : null}
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
          <span style={{ fontWeight: 800, cursor: "pointer" }} onClick={() => o.show_id && navigate(`/show/${o.show_id}`)}>{o.item_title}</span>
          <span className={`ss-pill ${pill.cls}`} data-testid={`offer-status-${o.id}`}>{pill.label}</span>
        </div>
        <div className="ss-muted" style={{ fontSize: 13, marginTop: 3 }}>
          {mode === "received" ? `From ${o.buyer_name}` : `To ${o.seller_name}`} · List {money(o.list_price)}
        </div>
        <div style={{ marginTop: 6, fontSize: 15 }}>
          Offer: <b>{money(o.amount)}</b>{o.counter_amount ? <> · Counter: <b style={{ color: "var(--brand)" }}>{money(o.counter_amount)}</b></> : null}
        </div>
        {o.message ? <div className="ss-muted" style={{ fontSize: 13, marginTop: 4, fontStyle: "italic" }}>“{o.message}”</div> : null}
        {canRespond && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button className="ss-btn ss-btn-sm ss-btn-primary" onClick={() => onAction("accept", o)} data-testid={`offer-accept-${o.id}`}><Check size={15} /> Accept {money(o.counter_amount || o.amount)}</button>
            <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => onAction("counter", o)} data-testid={`offer-counter-${o.id}`}><Repeat size={15} /> Counter</button>
            <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)" }} onClick={() => onAction("decline", o)} data-testid={`offer-decline-${o.id}`}><X size={15} /> Decline</button>
          </div>
        )}
        {o.status === "accepted" && o.order_id && (
          <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ marginTop: 10 }} onClick={() => navigate("/purchases")} data-testid={`offer-order-${o.id}`}>View order</button>
        )}
      </div>
    </div>
  );
}

export default function Offers() {
  const [tab, setTab] = useState("sent");
  const [sent, setSent] = useState(null);
  const [received, setReceived] = useState(null);

  const load = () => {
    api.get("/me/offers").then((d) => setSent(d.offers)).catch(() => setSent([]));
    api.get("/me/offers/received").then((d) => setReceived(d.offers)).catch(() => setReceived([]));
  };
  useEffect(() => { load(); }, []);
  if (sent === null || received === null) return <Loading />;

  const onAction = async (action, o) => {
    try {
      if (action === "counter") {
        const val = window.prompt(`Counter offer for "${o.item_title}" (list ${money(o.list_price)}):`, String(o.amount));
        if (val === null) return;
        const amount = Number(val);
        if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
        await api.post(`/offers/${o.id}/counter`, { amount });
        toast.success(`Countered at ${money(amount)}`);
      } else if (action === "accept") {
        if (!window.confirm(`Accept ${money(o.counter_amount || o.amount)} for "${o.item_title}"? This creates an order.`)) return;
        await api.post(`/offers/${o.id}/accept`);
        toast.success("Offer accepted — order created");
      } else if (action === "decline") {
        await api.post(`/offers/${o.id}/decline`);
        toast.success("Offer declined");
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const list = tab === "sent" ? sent : received;
  return (
    <div className="fade-up">
      <PageHead title="Offers" sub="Negotiate buy-now prices with sellers." />
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button className={`ss-btn ss-btn-sm ${tab === "sent" ? "ss-btn-primary" : "ss-btn-ghost"}`} onClick={() => setTab("sent")} data-testid="offers-tab-sent">Sent ({sent.length})</button>
        <button className={`ss-btn ss-btn-sm ${tab === "received" ? "ss-btn-primary" : "ss-btn-ghost"}`} onClick={() => setTab("received")} data-testid="offers-tab-received">Received ({received.length})</button>
      </div>
      {list.length === 0 ? (
        <Empty icon={Tag} title={tab === "sent" ? "No offers sent" : "No offers received"} sub={tab === "sent" ? "Make an offer on a buy-now item to negotiate." : "Buyer offers on your items will appear here."} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="offers-list">
          {list.map((o) => <OfferCard key={o.id} o={o} mode={tab === "sent" ? "sent" : "received"} onAction={onAction} />)}
        </div>
      )}
    </div>
  );
}
